import { Message } from "@bbwt/classes";
import { IPermission } from "@main/permissions/permission";
import { MessageService } from "primeng/api";
import { UserAccess } from ".";

export function getDefaultPermissions(
    userAccess: UserAccess,
    permissionOptions: IPermission[],
    defaultPermissions: { [roleId: string]: string[] },
    messageService: MessageService,
    resetPermissions = false
): IPermission[] {
    const selectedRoleIds = (userAccess.roles ?? []).map(r => r.id);
    if (selectedRoleIds.length === 0) {
        return [];
    }

    const permissionsFromRoles: IPermission[] = [];

    for (const roleId of selectedRoleIds) {
        const permissionNames = defaultPermissions[roleId] ?? [];
        for (const permission of permissionNames) {
            const permissionOpt = permissionOptions.find(p => p.name === permission);
            if (permissionOpt && !permissionsFromRoles.includes(permissionOpt)) {
                permissionsFromRoles.push(permissionOpt);
            }
        }
    }

    if (permissionsFromRoles.length > 0) {
        let mergedOrResetPermissions = permissionsFromRoles;
        const currentPermissions = userAccess.permissions ?? [];
        if (!resetPermissions) {
            mergedOrResetPermissions = [
                ...currentPermissions,
                ...mergedOrResetPermissions.filter(p => !currentPermissions.some(cp => cp.id === p.id))
            ];
        }

        return mergedOrResetPermissions;
    } else if (resetPermissions) {
        messageService.add(Message.Warning("No default permissions found for the selected roles. Keeping selected (if any) permissions."));
        return userAccess.permissions;
    }

    return userAccess.permissions ?? [];
}

/**
 * Rebuilds permissions from the current role defaults while optionally preserving
 * permissions the user added on top of the last applied role-default set.
 */
export function resolvePermissionsOnRoleChange(
    roleDefaults: IPermission[],
    currentPermissions: IPermission[] | null | undefined,
    previousDefaultPermissionIds: Set<string>,
    resetPermissions: boolean,
    hasSelectedRoles: boolean
): { permissions: IPermission[]; nextDefaultPermissionIds: Set<string> } {
    if (!hasSelectedRoles) {
        return { permissions: [], nextDefaultPermissionIds: new Set() };
    }

    const nextDefaultPermissionIds = new Set(roleDefaults.map(permission => permission.id));

    if (resetPermissions) {
        return { permissions: [...roleDefaults], nextDefaultPermissionIds };
    }

    const customPermissions = (currentPermissions ?? []).filter(
        permission => !previousDefaultPermissionIds.has(permission.id)
    );

    const permissions = [...roleDefaults];
    for (const customPermission of customPermissions) {
        if (!permissions.some(permission => permission.id === customPermission.id)) {
            permissions.push(customPermission);
        }
    }

    return { permissions, nextDefaultPermissionIds };
}
